/* eslint-disable import/prefer-default-export */

import clsx from 'clsx';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  CircleCheckBig,
  FileX,
  Hourglass,
  MapPin,
  ShieldCheck,
  SquareUserRound,
} from 'lucide-react';
import Image from 'next/image';
import toast from 'react-hot-toast';
import { SAMPLE_IMAGE } from '@/utils/sample/img';
import { useJobs } from '@/context/JobsContext';
import { convertIndustry, convertOppStructure, convertType } from '../../utils';
import styles from './styles.module.css';

// Status badge component
function StatusBadge({ status = '' }) {
  const getStatusConfig = (status) => {
    const configs = {
      applied: {
        icon: CircleCheckBig,
        color: 'green',
        label: 'Applied',
      },
      got_interview: {
        icon: SquareUserRound,
        color: 'green',
        label: 'Got Interview',
      },
      got_offer: {
        icon: ShieldCheck,
        color: 'green',
        label: 'Got Offer',
      },
      rejected: {
        icon: FileX,
        color: 'gray',
        label: 'Not Selected',
      },
    };
    return configs[status] || null;
  };

  const config = getStatusConfig(status);
  const Icon = config?.icon;

  return (
    config && (
      <div
        className={clsx(
          styles.statusBadge,
          styles[
            `statusBadge${
              config.color.charAt(0).toUpperCase() + config.color.slice(1)
            }`
          ],
        )}
      >
        <Icon size={14} className={styles.statusIcon} />
        <span className={styles.statusText}>{config.label}</span>
      </div>
    )
  );
}

export function JobDetails({ data }) {
  const { jobStages } = useJobs();

  // Get current job status from context
  const getCurrentJobStatus = () => {
    if (!data?.id || !jobStages) return null;

    const jobId = data.id;
    const stages = Object.keys(jobStages);
    const currentStage = stages.find((stage) => jobStages[stage].includes(jobId));

    return currentStage || null;
  };

  const renderTop = () => {
    const date = data?.deadline !== null ? parseISO(data?.deadline) : new Date();
    const relativeTime = formatDistanceToNow(date, { addSuffix: true });
    const currentStatus = getCurrentJobStatus();

    return (
      <div className={styles.top}>
        <div className={styles.roleContainer}>
          <Image src={SAMPLE_IMAGE} height={48} width={48} />
          <div className={styles.roleDetails}>
            <h2>{data?.title}</h2>
            <div className={styles.lowerDetail}>
              <p>{data?.company}</p>
              <p className={styles.date}>
                {data?.deadline ? relativeTime : 'No Deadline'}
              </p>
            </div>
          </div>
        </div>
        {currentStatus && <StatusBadge status={currentStatus} />}
        {/* {renderBookmark()} */}
      </div>
    );
  };

  const renderMiddle = () => {
    const oppStructureArray = convertOppStructure(data?.opp_structure);
    const typeArray = convertType(data?.type);
    const industryArray = convertIndustry(data?.industry);

    return (
      <div className={styles.middle}>
        <div className={styles.snapshot}>
          <div className={clsx(styles.date, styles.chip)}>
            <Hourglass size={16} className={styles.dateIcon} />
            <p>
              Due:
              {data?.deadline
                ? formatDistanceToNow(parseISO(data.deadline), {
                  addSuffix: true,
                })
                : 'No Deadline'}
            </p>
          </div>
          <div className={clsx(styles.location, styles.chip)}>
            <MapPin size={16} className={styles.locationIcon} />
            <p>{data?.location || 'No Location'}</p>
          </div>
          <div className={clsx(styles.oppStructure, styles.chip)}>
            {oppStructureArray.map((item, index) => (
              <p
                key={`${data?.id}_oppStructure_${index}`}
                className={styles.tag}
              >
                {item}
              </p>
            ))}
          </div>
          {typeArray.length > 0 && (
            <div className={clsx(styles.type, styles.chip)}>
              {typeArray.map((item, index) => (
                <p key={`${data?.id}_type_${index}`} className={styles.tag}>
                  {item}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className={styles.section}>
          <h3>Description</h3>
          <p>{data?.description}</p>
        </div>
        {/* <div className={styles.industry}>
          {industryArray.map((item, index) => (
            <div key={index} className={styles.tag}>
              {item.replace(/_/g, ' ')}
            </div>
          ))}
        </div> */}
        <div className={styles.section}>
          <h3>Details</h3>
          <ul>
            {data?.responsibilities?.map((responsibility, index) => (
              <li key={index}>{responsibility}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderBottom = () => {
    return (
      <div className={styles.bottom}>
        <div className={styles.section}>
          <h3>Skills</h3>
          <div className={styles.skills}>
            {data?.qualifications?.map((skill, index) => (
              <div key={index} className={styles.skill}>
                {skill}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.content}>
      {renderTop()}
      {renderMiddle()}
      {renderBottom()}
    </div>
  );
}

export function Redirect({ jobLink }) {
  const handleOpenLink = () => {
    if (jobLink !== null) {
      window.open(jobLink, '_blank');
    } else {
      toast.error('Job link is not available');
    }
  };

  return (
    <div className={styles.placeholder}>
      <div className={styles.title}>
        <h3>Ready to Apply?</h3>
        <p>Visit the link below to apply for this opportunity.</p>
      </div>

      <button type="button" onClick={handleOpenLink} className={styles.button}>
        Go to Application
      </button>
    </div>
  );
}

export function Spinner() {
  return (
    <div className={styles.placeholder}>
      <div className={styles.title}>
        <p>Loading...</p>
      </div>
    </div>
  );
}
